import React from 'react';
import { Link } from 'react-router';
import Moment from 'moment';
import Api from '../../api/api';
import AppStore from '../../stores/stores';
import AuthMixin from '../../mixins/authenticationMixin';
import AppRoutes from '../../routes/routes';
import DataError from '../../components/errors/data-error';
import SEOHead from '../../components/seo/seo-head';
import CustomSelect from '../../components/custom-elements/custom-select';

import './notes-marketing.scss';

class NotesMarketing extends React.Component {
    constructor() {
        super();
        this.state = {
            noData: false,
            notes: [],
            error: false,
            sortBy: 'date',
            expandedNotes: {}
        };
        this.handleSortChange = this.handleSortChange.bind(this);
        this.toggleNote = this.toggleNote.bind(this);
    }

    componentDidMount() {
        this.loadNotes();
    }

    loadNotes() {
        AppStore.getNotes(this.props.userApiRequest, {
            onSuccess: ({ data }) => {
                this.setState({
                    notes: this.sortByDate(data),
                    noData: data.length === 0
                });
            },
            onError: (details) => {
                if ('Rollbar' in window) {
                    Rollbar.error('Failed to load notes view', details);
                }
                this.setState({ error: true });
            }
        });
    }

    sortByDate(data) {
        return [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    sortByAlphabet(data) {
        return [...data].sort((a, b) =>
            a.checklist_data.title.localeCompare(b.checklist_data.title)
        );
    }

    handleSortChange(e) {
        let targetValue = '';

        if (Api.selectorMatches(e.currentTarget, 'li')) {
            targetValue = e.currentTarget.textContent;
        } else if (Api.selectorMatches(e.currentTarget, 'select')) {
            targetValue = e.currentTarget.value;
        }

        if (targetValue !== this.state.sortBy) {
            const firstChar = targetValue.charAt(0).toUpperCase();
            const remainingChars = targetValue.substring(1);
            const sortFn = this[`sortBy${firstChar}${remainingChars}`];
            const sorted = typeof sortFn === 'function' ? sortFn.call(this, this.state.notes) : this.state.notes;
            this.setState({ sortBy: targetValue, notes: sorted });
        }
    }

    toggleNote(slug) {
        this.setState(prev => ({
            expandedNotes: {
                ...prev.expandedNotes,
                [slug]: !prev.expandedNotes[slug]
            }
        }));
    }

    render() {
        const { error, noData, notes, expandedNotes } = this.state;

        if (error) return <DataError />;

        const visibleNotes = notes.filter(n => n.content && n.content !== '');
        const noteCount = visibleNotes.length;

        return (
            <div className="notes-marketing-page">
                <SEOHead title="My Notes | Neurochecklists" description="View and manage all your notes across checklists." />

                {/* Header */}
                <section className="notes-mkt-hero">
                    <div className="notes-mkt-hero-content">
                        <div className="notes-mkt-title-row">
                            <div>
                                <h1>My Notes</h1>
                                <p className="notes-mkt-subtitle">Notes you've saved across checklists, all in one place.</p>
                            </div>
                            {noteCount > 0 && (
                                <div className="notes-count-badge">{noteCount} note{noteCount !== 1 ? 's' : ''}</div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Main Content */}
                <div className="notes-mkt-body">
                    {!noData && noteCount > 0 && (
                        <div className="notes-sort-bar" id="custom-element" onClick={this.handleSortChange.bind(this)}>
                            <span className="notes-sort-label">Sort by:</span>
                            <div className="custom-select-holder">
                                <CustomSelect
                                    handleChange={(e) => this.handleSortChange(e)}
                                    default={'date'}
                                    options={{
                                        date: 'Date',
                                        alphabet: 'Alphabet'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {noData ? (
                        <div className="notes-empty-state">
                            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                            </svg>
                            <h2>No notes yet</h2>
                            <p>When you write notes on a checklist, they'll appear here for easy reference.</p>
                            <Link to={AppRoutes.explore} className="notes-empty-cta">Browse Checklists →</Link>
                        </div>
                    ) : (
                        <div className="notes-list">
                            {visibleNotes.map(note => {
                                const slug = note.checklist_data.slug;
                                const isOpen = !!expandedNotes[slug];
                                return (
                                    <article key={slug} className="note-card">
                                        <div className="note-card-header">
                                            <div className="note-card-meta">
                                                <Link
                                                    to={`${AppRoutes.checklists}${slug}`}
                                                    className="note-card-title"
                                                >
                                                    {note.checklist_data.title}
                                                </Link>
                                                <span className="note-card-date">
                                                    {Moment(note.created_at).format('D MMM YYYY, h:mma')}
                                                </span>
                                            </div>
                                            <button
                                                className={`note-expand-btn${isOpen ? ' open' : ''}`}
                                                onClick={() => this.toggleNote(slug)}
                                                aria-label={isOpen ? 'Collapse note' : 'Expand note'}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>
                                        </div>
                                        {isOpen && (
                                            <div
                                                className="note-card-content ql-snow"
                                                dangerouslySetInnerHTML={{ __html: note.content }}
                                            />
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default AuthMixin(NotesMarketing);
